import React, { useState } from "react";
import "./ContactUs.css";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faInstagram, faTiktok } from "@fortawesome/free-brands-svg-icons";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function ContactUs() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Posts to the API, which stores the enquiry and notifies the admins. The old
  // implementation opened a mailto: link, so nothing ever reached Ardency unless the
  // visitor happened to have a mail client set up and sent it themselves.
  const handleSubmit = async (e) => {
    e.preventDefault();

    const fullName = formData.fullName.trim();
    const email = formData.email.trim();
    const phoneNumber = formData.phoneNumber.trim();
    const message = formData.message.trim();

    if (!fullName || !email || !phoneNumber || !message) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Form",
        text: "Please fill in all fields before submitting.",
        confirmButtonColor: "#0d7ea8",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ContactEnquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, email, phoneNumber, message }),
      });

      const result = await response.json().catch(() => null);

      if (response.ok) {
        setFormData({ fullName: "", email: "", phoneNumber: "", message: "" });
        Swal.fire({
          icon: "success",
          title: "Enquiry Sent",
          text:
            result?.message ||
            "Thank you for reaching out. We have received your enquiry and will be in touch shortly.",
          confirmButtonColor: "#0d7ea8",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Could Not Send",
          text: result?.message || `Something went wrong (${response.status}). Please try again.`,
          confirmButtonColor: "#0d7ea8",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could Not Send",
        text: "We could not reach the server. Please check your connection and try again.",
        confirmButtonColor: "#0d7ea8",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-us-container">
      <div className="contact-us-background-gradient contact-us-gradient-1"></div>
      <div className="contact-us-background-gradient contact-us-gradient-2"></div>

      <div className="contact-us-content-wrapper">
        {/* Form Section */}
        <section className="contact-us-form-section">
          <div className="contact-us-form-container">
            <h2 className="contact-us-form-heading">Get in Touch</h2>

            <form onSubmit={handleSubmit}>
              <div className="contact-us-input-group">
                <label className="contact-us-input-label" htmlFor="fullName">
                  Full Name
                </label>
                <input
                  className="contact-us-input-field"
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
                <svg
                  className="contact-us-field-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>

              <div className="contact-us-input-group">
                <label className="contact-us-input-label" htmlFor="email">
                  Email Address
                </label>
                <input
                  className="contact-us-input-field"
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                />
                <svg
                  className="contact-us-field-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>

              <div className="contact-us-input-group">
                <label className="contact-us-input-label" htmlFor="phoneNumber">
                  Phone Number
                </label>
                <input
                  className="contact-us-input-field"
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  required
                  pattern="^01[0-9]{8,9}$"
                />
                <svg
                  className="contact-us-field-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
              </div>

              <div className="contact-us-input-group">
                <label className="contact-us-input-label" htmlFor="message">
                  Message
                </label>
                <textarea
                  className="contact-us-input-field contact-us-textarea"
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="What do you have in mind?"
                  required
                ></textarea>
                <svg
                  className="contact-us-field-icon contact-us-textarea-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="21" y1="10" x2="3" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="21" y1="18" x2="3" y2="18"></line>
                </svg>
              </div>

              <button
                className="contact-us-submit-button"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Submit"}
              </button>
            </form>
          </div>
        </section>

        {/* Info Section */}
        <section className="contact-us-info-section">
          <div className="contact-us-info-container">
            <h2 className="contact-us-info-heading">Reach Us At</h2>

            <p className="contact-us-info-text">
              <span className="contact-label">Address</span>
              <span className="contact-number">No.21, Jalan Wawasan 2C/KU7, Sungai Kapar Indah, 42200 Kapar,
                Selangor</span>

            </p>

            <p className="contact-us-info-text">
              <span className="contact-label">Contact</span>
              <a href={`https://wa.me/+60124825199`} className="contact-us-footer-phone-link" target="_blank"> 012-482 5199</a> /
              <a href={`https://wa.me/+60179765688`} className="contact-us-footer-phone-link" target="_blank"> 017-976 5688</a> /
              <a href={`https://wa.me/+60175327288`} className="contact-us-footer-phone-link" target="_blank"> 017-532 7288</a> /
              <a href={`https://wa.me/+60126308368`} className="contact-us-footer-phone-link" target="_blank"> 012-630 8368</a>
            </p>

            <p className="contact-us-info-text">
              <span className="contact-label">Business Hours</span>
              <span className="contact-number">Monday - Friday | 9am - 6pm</span>
            </p>

            <div className="contact-us-social-links">
              <a
                className="contact-us-social-link"
                href="https://www.facebook.com/p/Ardency-Construction-and-Trading-Sdn-Bhd-100049133691033/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FontAwesomeIcon
                  icon={faFacebook}
                  className="contactus-fb-icon"
                />
              </a>

              <a
                className="contact-us-social-link"
                href="https://www.instagram.com/ardency_construction/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FontAwesomeIcon
                  icon={faInstagram}
                  className="contactus-ig-icon"
                />
              </a>

              <a
                className="contact-us-social-link"
                href="https://www.tiktok.com/@ardency_construction"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
              >
                <FontAwesomeIcon
                  icon={faTiktok}
                  className="contactus-tiktok-icon"
                />
              </a>

              <a
                className="contact-us-social-link"
                href="https://www.xiaohongshu.com/user/profile/65fce81b000000000600f0f6"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="XHS"
              >
                <span className="contactus-xhs-icon">
                  <img
                    src="./xhs.svg"
                    style={{ width: '25px', height: '25px', marginTop: '8px' }}
                    alt="XiaoHongShu"
                  />
                </span>
              </a>

              <a
                className="contact-us-social-link"
                href="https://s.lemon8-app.com/al/GghbQwUFUe"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="lemon8"
              >
                <span className="contactus-xhs-icon">
                  <img
                    src="./lemon8.svg"
                    style={{ width: '25px', height: '25px', marginTop: '8px' }}
                    alt="lemon8"
                  />
                </span>
              </a>
            </div>

            <div className="contact-us-map-container">
              <iframe
                className="contact-us-map"
                src="https://maps.google.com/maps?q=Ardency Construction and Trading Sdn. Bhd., 21, Jalan Wawasan 2C/KU 7 Sungai Kapar Indah Kapar, 42200 Klang, Selangor&t=&z=13&ie=UTF8&iwloc=&output=embed"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ardency Construction and Trading Location"
              ></iframe>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default ContactUs;
